import { useContext, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Card, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { startCase } from 'lodash';
import { PriceDisplay } from './price-display';
import { MarkdownDescription } from './markdown-description';
import { GlobalContext } from '../contexts/global-context';
import { InlineLoading } from './standard-loading';
import BlueprintGrid from './blueprint-grid';

export function FeaturedPlanCard({ model }) {
    const navigate = useNavigate();
    const { isMobile } = useContext(GlobalContext);

    const modelUrl = model.base_url ? `${model.base_url}model.glb` : null;
    const [modelLoading, setModelLoading] = useState(true);
    const modelViewerRef = useRef(null);

    const handleModelLoad = useCallback((node) => {
        if (node && node !== modelViewerRef.current) {
            modelViewerRef.current = node;
            node.addEventListener('load', () => setModelLoading(false));
            node.addEventListener('error', () => setModelLoading(false));
        }
    }, []);

    const handleClick = () => {
        navigate(`/designs/${model.name}`);
    };

    return (
        <Card
            onClick={handleClick}
            elevation={0}
            sx={{
                width: '100%',
                maxWidth: '1100px',
                backgroundColor: '#FFFFFF',
                borderRadius: '18px',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                cursor: 'pointer',
                transform: 'translate3d(0, 0, 0)',
                transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                '&:hover': {
                    transform: 'translate3d(0, -2px, 0)',
                    boxShadow: '0 4px 14px rgba(33,31,28,.18)',
                },
            }}
        >
            {/* 3D Model Section */}
            <BlueprintGrid
                sx={{
                    position: 'relative',
                    width: { xs: '100%', md: '50%' },
                    aspectRatio: { xs: '4/3', md: '4/3' },
                    overflow: 'hidden',
                    flexShrink: 0,
                }}
            >
                {/* Difficulty badge */}
                {model.difficulty && (
                    <Chip
                        label={startCase(model.difficulty)}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            zIndex: 2,
                            backgroundColor: 'rgba(12,21,38,.85)',
                            color: '#e8ddd0',
                            border: '1px solid rgba(196,164,127,.16)',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 26,
                            backdropFilter: 'blur(4px)',
                        }}
                    />
                )}

                {modelUrl && (
                    <>
                        <model-viewer
                            ref={handleModelLoad}
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                            src={modelUrl}
                            alt={startCase(model.name)}
                            shadow-intensity="1"
                            shadow-softness="0.3"
                            environment-image="legacy"
                            exposure="0.5"
                            camera-controls
                            camera-orbit={isMobile ? "0deg 75deg 85%" : "0deg 75deg 80%"}
                            loading="lazy"
                            touch-action="pan-y"
                            interaction-prompt="auto"
                            onClick={(e) => e.stopPropagation()}
                        />
                        {modelLoading && (
                            <Box sx={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                zIndex: 1,
                            }}>
                                <InlineLoading />
                            </Box>
                        )}
                    </>
                )}
            </BlueprintGrid>

            {/* Details Section */}
            <Box
                sx={{
                    width: { xs: '100%', md: '50%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    px: { xs: '22px', md: '36px' },
                    py: { xs: '22px', md: '32px' },
                }}
            >
                {/* Eyebrow */}
                <Typography sx={{
                    fontSize: '12px',
                    letterSpacing: '0.24em',
                    textTransform: 'uppercase',
                    color: 'woodDark',
                    fontWeight: 600,
                    mb: 1,
                }}>
                    Featured Design
                </Typography>

                {/* Title */}
                <Typography
                    component="h3"
                    sx={{
                        fontSize: { xs: '20px', md: '24px' },
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.25,
                    }}
                >
                    {startCase(model.name)}
                </Typography>

                {/* Tags */}
                {(model.header_tags || model.header) && (
                    <Typography sx={{
                        color: 'ink3',
                        mt: 0.75,
                        fontSize: '13px',
                        lineHeight: 1.4,
                    }}>
                        {Array.isArray(model.header_tags)
                            ? model.header_tags.join(' · ')
                            : model.header}
                    </Typography>
                )}

                {/* Description */}
                {model.description && (
                    <Box sx={{
                        mt: 2,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 5, md: 6 },
                        WebkitBoxOrient: 'vertical',
                    }}>
                        <MarkdownDescription
                            content={model.description}
                            color="ink2"
                            variant="body2"
                        />
                    </Box>
                )}

                {/* Footer: Price + CTA */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 3,
                }}>
                    <Typography sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: 'text.primary',
                    }}>
                        <PriceDisplay amount={model.amount} />
                    </Typography>

                    <Button
                        variant="contained"
                        size={isMobile ? 'small' : 'medium'}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/designs/${model.name}`);
                        }}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '14px',
                            px: 2.5,
                            borderRadius: '10px',
                        }}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                    >
                        Customize
                    </Button>
                </Box>
            </Box>
        </Card>
    );
}
